import { Theme } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import Guid from "_shared/guids/guid";
import MarketplaceProfilesTable from "marketplace/_views/profiles/profile-table";
import { ProfileTableData } from "marketplace/_views/profiles/profile-table-data";
import { PageTab } from "marketplace/_views/profiles/profiles-page-tabs";
import { editComponentMap, viewComponentMap } from "marketplace/_views/profiles/profile-page-components";
import { MarketplaceProfileStore } from "marketplace/_views/profiles/use-marketplace-profile-store";
import {
  buildVisibilityConfirmMessage,
  buildVisibilityConfirmTitle,
  buildVisibilityErrorMessage,
  buildVisibilitySuccessMessage,
} from "marketplace/_views/profiles/visibility-toggle-switch";
import { companiesApi } from "marketplace/companies/_api/company-api";
import Company from "marketplace/companies/company";
import CompanyProfile from "marketplace/companies/profiles/company-profile";
import { individualsApi } from "marketplace/individuals/_api/individual-api";
import Individual from "marketplace/individuals/individual";
import IndividualProfile from "marketplace/individuals/profiles/individual-profile";
import TeamProfile from "marketplace/team-profile/team-profile";
import {
  useDeleteTeamMutation,
  useRemoveTeamMemberMutation,
  useUpdateTeamMutation,
} from "marketplace/teams/_api/marketplace-team-api";
import Team from "marketplace/teams/team";
import { enqueueSnackbar } from "notistack";
import React, { useCallback, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";
import { useSession } from "users/__session/session-context";

/**
 * Names the dialog a marketplace profile route asks for.
 *
 * The effect below opens that dialog, and it runs again whenever this page re-renders past a
 * changed dependency or remounts — neither of which means the user asked for the profile a second
 * time. Without a name to recognize the repeat by, the second open lands on top of whatever the
 * user opened in the meantime, and the dialog stack marks that one `open: false`: it stays in the
 * DOM under `keepMounted`, goes `aria-hidden` and inert, and is gone as far as the user and the
 * accessibility tree are concerned. Opening a team's fee schedule editor from its profile form lost
 * the editor that way.
 *
 * The route is the identity, so the same profile opened for a different action — or a different
 * profile — is a different dialog and still opens.
 */
function routeDialogKey(profileType: PageTab, action: "create" | "edit" | "view", profileId?: Guid): string {
  return `marketplace-profile:${profileType}:${action}:${profileId?.value ?? ""}`;
}

type ProfileTypePageProps = {
  activeTab: PageTab;
  addProfileDisabled: boolean;
  addProfileDisabledMessage: string;
  getShouldDisableRowEditing: (profile: ProfileTableData) => boolean;
  profileStore: MarketplaceProfileStore;
};

export default function ProfileTypePage(props: Readonly<ProfileTypePageProps>) {
  const {
    activeTab,
    addProfileDisabled,
    addProfileDisabledMessage,
    getShouldDisableRowEditing,
    profileStore,
  } = props;

  const isDirtyRef = React.useRef<boolean>(false);
  const isTransitioningToEditRef = React.useRef<boolean>(false);
  const isClosingAfterSaveRef = React.useRef<boolean>(false);

  const routeParams = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const confirm = useConfirmDialog();
  const { openDialog } = useDialog();
  const dispatch = useAttorneyHubDispatch();
  const session = useSession();
  const isMediumDisplaySize = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));

  const [updateTeamMutation] = useUpdateTeamMutation();
  const [deleteTeamMutation] = useDeleteTeamMutation();
  const [removeTeamMemberMutation] = useRemoveTeamMemberMutation();
  const [updateCompanyMutation] = companiesApi.useUpdateCompanyMutation();

  const saveAndCloseProfile = useCallback(async () => {
    isClosingAfterSaveRef.current = true;

    try {
      const saveButton = document.getElementById("saveButton") as HTMLButtonElement | null;
      if (!saveButton || saveButton.disabled) {
        throw new Error("Profile is not ready to save");
      }

      saveButton.click();

      await new Promise<void>((resolve, reject) => {
        let attempts = 0;
        const maxAttempts = 120;

        const intervalId = globalThis.setInterval(() => {
          attempts += 1;

          if (!isDirtyRef.current) {
            globalThis.clearInterval(intervalId);
            resolve();
            return;
          }

          if (attempts >= maxAttempts) {
            globalThis.clearInterval(intervalId);
            reject(new Error("Profile save did not complete"));
          }
        }, 50);
      });
    } catch {
      enqueueSnackbar("Unable to save profile. Please try again.", { variant: "error" });
    } finally {
      isClosingAfterSaveRef.current = false;
    }
  }, []);

  const createProfile = useCallback(() => {
    const ProfileComponent = editComponentMap[activeTab];
    const parentURLString = `/marketplace/profiles/${activeTab}`;

    openDialog({
      dialogKey: routeDialogKey(activeTab, "create"),
      titleStyle: {
        paddingBottom: 0,
      },
      // A flex content area gives the form a height to work with, so a long form can scroll inside
      // the dialog and keep its save button in view.
      contentSxProps: {
        display: "flex",
      },
      MuiProps: {
        maxWidth: activeTab === PageTab.Individual ? "lg" : "md",
        fullWidth: true,
        style: {
          overflowX: "hidden",
          paddingBottom: 0,
        },
      },
      onClose: () => {
        isDirtyRef.current = false;
        navigate(parentURLString);
      },
      onSave: async () => {
        await saveAndCloseProfile();
      },
      isDirty: isDirtyRef.current,
      isDirtyRef,
      component: (
        <ProfileComponent
          navigate={navigate}
          searchParams={searchParams}
          setSearchParams={setSearchParams}
          isVisible={false}
          onSave={(profile) => {
            if (!(profile instanceof Team)) return;
            if (isClosingAfterSaveRef.current) {
              isDirtyRef.current = false;
              return;
            }
            isDirtyRef.current = false;
            isTransitioningToEditRef.current = true;
            navigate(`${parentURLString}/edit/${profile.id}`, { replace: true, state: { exemptFromClose: true } });
          }}
          onFormDirty={(isDirty?: boolean) => {
            isDirtyRef.current = isDirty ?? true;
          }}
        />
      ),
    });
  }, [activeTab, navigate, openDialog, saveAndCloseProfile, searchParams, setSearchParams]);

  const openEditProfileDialog = useCallback(
    (profileType: PageTab, profileId: Guid, profileData?: IndividualProfile & CompanyProfile & TeamProfile) => {
      const ProfileComponent = editComponentMap[profileType];
      const parentURLString = `/marketplace/profiles/${profileType}`;

      openDialog({
        dialogKey: routeDialogKey(profileType, "edit", profileId),
        titleStyle: {
          paddingBottom: 0,
        },
        // A flex content area gives the form a height to work with, so a long form can scroll inside
        // the dialog and keep its save button in view.
        contentSxProps: {
          display: "flex",
        },
        MuiProps: {
          maxWidth: profileType === PageTab.Individual ? "lg" : "md",
          fullWidth: true,
          style: {
            overflowX: "hidden",
            paddingBottom: 0,
          },
        },
        onClose: () => {
          isDirtyRef.current = false;
          navigate(parentURLString);
        },
        onSave: async () => {
          await saveAndCloseProfile();
        },
        isDirty: isDirtyRef.current,
        isDirtyRef,
        component: (
          <ProfileComponent
            navigate={navigate}
            searchParams={searchParams}
            setSearchParams={setSearchParams}
            individualId={profileType === PageTab.Individual ? profileId : undefined}
            companyId={profileType === PageTab.Company ? profileId : undefined}
            teamId={profileType === PageTab.Teams ? profileId : undefined}
            profile={profileData}
            onFormDirty={(isDirty?: boolean) => {
              isDirtyRef.current = isDirty ?? true;
            }}
          />
        ),
      });
    },
    [navigate, openDialog, saveAndCloseProfile, searchParams, setSearchParams],
  );

  const openViewProfileDialog = useCallback(
    (profileType: PageTab, profileId: Guid, profileData?: IndividualProfile & CompanyProfile & TeamProfile) => {
      if (!profileId) {
        enqueueSnackbar(`${profileType} profile unavailable.`, { variant: "error" });
        return;
      }

      const ProfileComponent = viewComponentMap[profileType];
      const parentURLString = `/marketplace/profiles/${activeTab}`;

      openDialog({
        dialogKey: routeDialogKey(profileType, "view", profileId),
        titleStyle: {
          paddingBottom: 0,
        },
        MuiProps: {
          maxWidth: profileType === PageTab.Individual ? "lg" : "md",
          fullWidth: true,
          style: {
            overflowX: "hidden",
            paddingBottom: 0,
          },
        },
        onClose: () => {
          navigate(parentURLString);
        },
        component: (
          <ProfileComponent individualId={profileId} companyId={profileId} teamId={profileId} profile={profileData} />
        ),
      });
    },
    [activeTab, navigate, openDialog],
  );

  useEffect(() => {
    if (!session.user?.legalEntityId) return;
    if (routeParams.pageTab !== activeTab) return;

    const action = routeParams.action;
    const id = routeParams.id;

    if (action === "create") createProfile();

    if (id) {
      if (action === "edit") {
        if (isTransitioningToEditRef.current) {
          isTransitioningToEditRef.current = false;
          return;
        }
        openEditProfileDialog(activeTab, new Guid(id));
      }
      if (action === "view") {
        openViewProfileDialog(activeTab, new Guid(id));
      }
    }
  }, [
    activeTab,
    routeParams.pageTab,
    routeParams.action,
    routeParams.id,
    session.user?.legalEntityId,
    createProfile,
    openEditProfileDialog,
    openViewProfileDialog,
  ]);

  const editProfile = useCallback(
    (profile: ProfileTableData) => {
      const editingDisabled = getShouldDisableRowEditing(profile);

      if (editingDisabled) {
        enqueueSnackbar("You don't have permission to edit this profile. Opening as read-only.", {
          variant: "warning",
        });
      }

      navigate(`/marketplace/profiles/${activeTab}/${editingDisabled ? "view" : "edit"}/${profile.id}`);
    },
    [activeTab, getShouldDisableRowEditing, navigate],
  );

  const publishProfile = useCallback(
    async (profile: ProfileTableData) => {
      if (!profile.profile) return;
      const name =
        profile instanceof Individual
          ? `${profile.profile.firstName} ${profile.profile.lastName}`
          : profile.profile.name ?? "this";
      const nextIsVisible = !profile.isVisible;

      const response = await confirm({
        title: buildVisibilityConfirmTitle(nextIsVisible),
        message: buildVisibilityConfirmMessage(name, nextIsVisible),
      });

      if (response === ConfirmResponse.Cancel) return;

      try {
        profile.toggleVisibility();
        if (profile instanceof Company) {
          await updateCompanyMutation({
            companyId: profile.id.value,
            isVisible: profile.isVisible,
          }).unwrap();
        } else if (profile instanceof Individual) {
          await dispatch(
            individualsApi.endpoints.updateIndividual.initiate({
              individualId: profile.id.value,
              isVisible: profile.isVisible,
            }),
          ).unwrap();
        } else if (profile instanceof Team) {
          await updateTeamMutation({
            teamId: profile.id.value,
            originalTeam: { ...profile, isVisible: !profile.isVisible } as Team,
            updatedTeam: profile,
          }).unwrap();
        }

        enqueueSnackbar(buildVisibilitySuccessMessage(name, profile.isVisible), { variant: "info" });
      } catch {
        enqueueSnackbar(buildVisibilityErrorMessage(nextIsVisible), { variant: "error" });
      }
    },
    [confirm, dispatch, updateCompanyMutation, updateTeamMutation],
  );

  const deleteProfile = useCallback(
    async (profileData: ProfileTableData) => {
      if (!profileData.profile || !profileData.id) {
        throw new Error("Profile data is missing");
      }
      const name =
        profileData instanceof Individual
          ? `${profileData.profile.firstName} ${profileData.profile.lastName}`
          : profileData.profile.name;

      const response = await confirm({
        title: "Delete profile?",
        message: `Delete ${name} profile?`,
        okButtonText: "Delete",
      });

      if (response === ConfirmResponse.Cancel) return;

      try {
        await deleteTeamMutation({ teamId: profileData.id.value }).unwrap();
      } catch {
        enqueueSnackbar("Couldn't delete profile. Please try again", { variant: "error" });
      }
    },
    [confirm, deleteTeamMutation],
  );

  const leaveTeam = useCallback(
    async (profile: ProfileTableData) => {
      if (!profile.profile) return;

      const team = profile as Team;
      if (!team.id || !team?.members) return;

      const membership = team.members.find((m) => m.userId?.isEqualTo(session.user?.id));
      if (!membership?.userId) return;

      const response = await confirm({
        title: "Leave vendor-team?",
        message: `Leave ${team.profile.name} team?`,
        okButtonText: "Leave",
      });

      if (response === ConfirmResponse.Cancel) return;

      await removeTeamMemberMutation({
        teamId: team.id.value,
        memberIndividualId: membership.userId.value,
      }).unwrap();
    },
    [session.user?.id, confirm, removeTeamMemberMutation],
  );

  return (
    <MarketplaceProfilesTable
      activeTab={activeTab}
      addProfileDisabled={addProfileDisabled}
      addProfileDisabledMessage={addProfileDisabledMessage}
      canViewTeamProfiles={!!session.context?.viewingAsVendor}
      currentUserId={session.user?.id}
      getShouldDisableRowEditing={getShouldDisableRowEditing}
      isError={profileStore.isError}
      isLoading={profileStore.isLoading}
      isMediumDisplaySize={isMediumDisplaySize}
      onCreateProfile={() => navigate(`/marketplace/profiles/${activeTab}/create`)}
      onDeleteProfile={(profile) => {
        void deleteProfile(profile);
      }}
      onEditProfile={editProfile}
      onLeaveTeam={(profile) => {
        void leaveTeam(profile);
      }}
      onPublishProfile={(profile) => {
        void publishProfile(profile);
      }}
      onRetry={profileStore.refetch}
      onTabChange={(newValue) => navigate(`/marketplace/profiles/${newValue}`)}
      profiles={profileStore.profiles}
      rowCount={profileStore.rowCount}
      showSkeletons={profileStore.showSkeletons}
      viewingAsVendor={session.context?.viewingAsVendor}
    />
  );
}
